import { useQuery } from "@tanstack/react-query";
import { ClientsPage } from "components/feature";
import { SectionHeader } from "components/layout";
import { trpc } from "components/rpc/trpc";
import { ErrorMessage } from "components/ui/error-message";
import { InlineBanner } from "components/ui/inline-banner";
import { Spinner } from "components/ui/spinner";
import * as React from "react";
import { useCallback } from "react";
import { useRegionId } from "store/selectors/hooks";
import { IncomingTransferCard } from "./incoming-transfer-card";
import { OutgoingTransferCard } from "./outgoing-transfer-card";
import { ClientTransferRequestSummary } from "./transfer-types";

interface TransferListProps {
    title: string;
    isLoading: boolean;
    isError: boolean;
    emptyMessage: string;
    transfers: ClientTransferRequestSummary[];
    renderTransfer: (transfer: ClientTransferRequestSummary) => React.ReactNode;
}

const TransferList: React.FC<TransferListProps> = props => {
    const { title, isLoading, isError, emptyMessage, transfers, renderTransfer } = props;

    return (
        <div className="flex-1">
            <SectionHeader title={title} />
            {isLoading ? <Spinner /> : null}
            {isError ? (
                <ErrorMessage component={title}>Unable to load transfers.</ErrorMessage>
            ) : null}
            {!isLoading && !isError ? (
                transfers.length > 0 ? (
                    <ul className="space-y-2 md:space-y-4">
                        {transfers.map(transfer => (
                            <li key={transfer.id}>{renderTransfer(transfer)}</li>
                        ))}
                    </ul>
                ) : (
                    <InlineBanner type="info">{emptyMessage}</InlineBanner>
                )
            ) : null}
        </div>
    );
};

export const ClientTransfers: React.FunctionComponent = () => {
    const regionId = useRegionId();
    const trpcContext = trpc.useTRPC();

    const incoming = useQuery(
        trpcContext.clientTransfer.pendingIncoming.queryOptions({ regionId }),
    );
    const outgoing = useQuery(
        trpcContext.clientTransfer.pendingOutgoing.queryOptions({ regionId }),
    );

    const refreshTransfers = useCallback(async () => {
        await Promise.all([incoming.refetch(), outgoing.refetch()]);
    }, [incoming, outgoing]);

    const renderIncoming = useCallback(
        (transfer: ClientTransferRequestSummary) => (
            <IncomingTransferCard
                clientTransferRequest={transfer}
                onTransferChanged={refreshTransfers}
            />
        ),
        [refreshTransfers],
    );

    const renderOutgoing = useCallback(
        (transfer: ClientTransferRequestSummary) => (
            <OutgoingTransferCard
                clientTransferRequest={transfer}
                onTransferChanged={refreshTransfers}
            />
        ),
        [refreshTransfers],
    );

    return (
        <ClientsPage>
            <div>
                <div className="col main-body full-width min-h-screen bg-white px-4 py-2">
                    <div className="space-y-4 lg:space-y-0 lg:flex lg:space-x-4">
                        <TransferList
                            title="Incoming transfers"
                            isLoading={incoming.isLoading}
                            isError={incoming.isError}
                            emptyMessage="No incoming transfers"
                            transfers={incoming.data ?? []}
                            renderTransfer={renderIncoming}
                        />
                        <TransferList
                            title="Outgoing transfers"
                            isLoading={outgoing.isLoading}
                            isError={outgoing.isError}
                            emptyMessage="No outgoing transfers"
                            transfers={outgoing.data ?? []}
                            renderTransfer={renderOutgoing}
                        />
                    </div>
                </div>
            </div>
        </ClientsPage>
    );
};
